import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useRefreshOnFocus from '../hooks/useRefreshOnFocus';
import { formatDate, formatCurrency, formatBoolean } from '../utils/dateUtils';
import CustomSelect from './CustomSelect';
import { SortablePaginatedTable } from './common';
import { 
  Avatar, 
  Breadcrumbs,
  Box,
  Button,
  CircularProgress,
  Fade,
  Grid,
  LinearProgress,
  Paper,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Dashboard as DashboardIcon,
  Description as ContractsIcon,
  Assessment as AuditIcon,
  AccountBalance as TransactionsIcon,
  TrendingUp as ForecastIcon,
  People as AccountsIcon,
  Settings as SettingsIcon,
  NavigateNext as NavigateNextIcon,
  Group as UsersIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import '../styles/Dashboard.css';
import '../styles/Contracts.css';
import MainLayout from './MainLayout';

// Use React.lazy to load the AddContractForm component only when needed
const AddContractForm = lazy(() => {
  console.log('Lazy loading AddContractForm - will only load once when needed');
  return import('./AddContractForm');
});

// Simple container to avoid unnecessary re-renders
const FormContainer = ({ onCancel }) => {
  console.log('Rendering FormContainer');
  return (
    <div className="scrollable-content">
      <Suspense fallback={<div className="loading-container"><CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading form...</Typography>
      </div>}>
        <AddContractForm onCancel={onCancel} />
      </Suspense>
    </div>
  );
};

// Simple Error Boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    if (this.props.onError) {
      this.props.onError(error);
    }
    console.error("Error in component:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return null; // Let the parent handle the UI for errors
    }
    return this.props.children;
  }
}

// Get delivery status based on points_burden
const getDeliveryStatus = (pointsBurden) => {
  // If points_burden is negative, it means we're ahead (delivered more than expected)
  if (pointsBurden === null || pointsBurden === undefined) return null;
  
  return pointsBurden <= 0 ? 'on-track' : 'off-track';
};

// Render delivery status badge
const renderDeliveryStatus = (pointsBurden) => {
  const status = getDeliveryStatus(pointsBurden);
  if (!status) return 'N/A';

  return (
    <div className={`delivery-status ${status}`}>
      {status === 'on-track' ? (
        <>
          <CheckCircleIcon fontSize="small" sx={{ marginRight: '4px' }} />
          On Track
        </>
      ) : (
        <>
          <ErrorIcon fontSize="small" sx={{ marginRight: '4px' }} />
          Off Track
        </>
      )}
    </div>
  );
};

// Column definitions for Overview mode
const overviewColumns = [
  { 
    id: 'contractName', 
    label: 'Contract Name', 
    sortable: true,
    render: (row) => (
      <Link 
        to={`/contracts/${row.contractId}`} 
        style={{ 
          color: '#333333',
          textDecoration: 'underline',
          fontWeight: 500
        }}
      >
        {row.contractName}
      </Link>
    )
  },
  { 
    id: 'contractNumber', 
    label: 'Contract Number', 
    sortable: true,
    render: (row) => (
      <Link 
        to={`/contracts/${row.contractId}`} 
        style={{ 
          color: '#6200ee', 
          textDecoration: 'underline',
          fontWeight: 500
        }}
      >
        {row.contractNumber}
      </Link>
    )
  },
  { 
    id: 'businessUnit', 
    label: 'Business Unit', 
    sortable: true 
  },
  { 
    id: 'contractType', 
    label: 'Contract Type', 
    sortable: true,
    render: (row) => row.contractType.charAt(0).toUpperCase() + row.contractType.slice(1)
  },
  { 
    id: 'mrr', 
    label: 'MRR', 
    sortable: true,
    align: 'right',
    getValue: (row) => parseFloat(row.mrr) || 0,
    render: (row) => formatCurrency(row.mrr)
  },
  { 
    id: 'ltv', 
    label: 'LTV', 
    sortable: true,
    align: 'right',
    getValue: (row) => parseFloat(row.ltv) || 0,
    render: (row) => formatCurrency(row.ltv)
  },
  { 
    id: 'priority', 
    label: 'Priority', 
    sortable: true,
    getValue: (row) => {
      const tierMatch = row.priority?.match(/Tier (\d+)/i);
      return tierMatch ? parseInt(tierMatch[1], 10) : 999;
    }
  },
  { 
    id: 'pointsBurden', 
    label: 'Delivery', 
    sortable: true,
    getValue: (row) => row.pointsBurden !== null ? row.pointsBurden : 999,
    render: (row) => renderDeliveryStatus(row.pointsBurden)
  },
  { 
    id: 'nextInvoiceDate', 
    label: 'Next Invoice', 
    sortable: true,
    getValue: (row) => row.nextInvoiceDate ? new Date(row.nextInvoiceDate).getTime() : 0,
    render: (row) => formatDate(row.nextInvoiceDate)
  },
  { 
    id: 'nextTermRenewal', 
    label: 'Next Term Renewal', 
    sortable: true,
    getValue: (row) => row.nextTermRenewal ? new Date(row.nextTermRenewal).getTime() : 0,
    render: (row) => formatDate(row.nextTermRenewal)
  }
];

// Column definitions for Details mode
const detailsColumns = [
  { 
    id: 'contractName', 
    label: 'Contract Name', 
    sortable: true,
    render: (row) => (
      <Link 
        to={`/contracts/${row.contractId}`} 
        style={{ 
          color: '#333333',
          textDecoration: 'underline',
          fontWeight: 500
        }}
      >
        {row.contractName}
      </Link>
    )
  },
  { 
    id: 'contractNumber', 
    label: 'Contract Number', 
    sortable: true,
    render: (row) => (
      <Link 
        to={`/contracts/${row.contractId}`} 
        style={{ 
          color: '#6200ee', 
          textDecoration: 'underline',
          fontWeight: 500
        }}
      >
        {row.contractNumber}
      </Link>
    )
  },
  { 
    id: 'isInitialTerm', 
    label: 'Initial Term', 
    sortable: true,
    getValue: (row) => row.isInitialTerm ? 1 : 0,
    render: (row) => formatBoolean(row.isInitialTerm)
  },
  { 
    id: 'contractStart', 
    label: 'Contract Start', 
    sortable: true,
    getValue: (row) => row.contractStart ? new Date(row.contractStart).getTime() : 0,
    render: (row) => formatDate(row.contractStart)
  },
  { 
    id: 'currentTermStart', 
    label: 'Current Term Start', 
    sortable: true,
    getValue: (row) => row.currentTermStart ? new Date(row.currentTermStart).getTime() : 0,
    render: (row) => formatDate(row.currentTermStart)
  },
  { 
    id: 'currentTermEnd', 
    label: 'Current Term End', 
    sortable: true,
    getValue: (row) => row.currentTermEnd ? new Date(row.currentTermEnd).getTime() : 0,
    render: (row) => formatDate(row.currentTermEnd)
  },
  { 
    id: 'nextTermRenewal', 
    label: 'Next Term Renewal', 
    sortable: true,
    getValue: (row) => row.nextTermRenewal ? new Date(row.nextTermRenewal).getTime() : 0,
    render: (row) => formatDate(row.nextTermRenewal)
  },
  { 
    id: 'nextTermStart', 
    label: 'Next Term Start', 
    sortable: true,
    getValue: (row) => row.nextTermStart ? new Date(row.nextTermStart).getTime() : 0,
    render: (row) => formatDate(row.nextTermStart)
  },
  { 
    id: 'nextTermEnd', 
    label: 'Next Term End', 
    sortable: true,
    getValue: (row) => row.nextTermEnd ? new Date(row.nextTermEnd).getTime() : 0,
    render: (row) => formatDate(row.nextTermEnd)
  }
];

export const ContractsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true); // Initial page load
  const [tableLoading, setTableLoading] = useState(false); // Just for filter changes
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('overview'); // 'overview' or 'details'
  const [showAddContract, setShowAddContract] = useState(false); // Add contract form visibility
  const [metricsData, setMetricsData] = useState({
    totalActiveContracts: 0,
    totalMRR: 0,
    averageMRR: 0,
    averageLTV: 0,
    tier1Contracts: 0,
    tier1Percentage: 0,
    totalPointsDelivered: 0
  });
  const [contracts, setContracts] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [priorityTiers, setPriorityTiers] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    businessUnit: 'all',
    contractType: 'all',
    priority: 'all',
    hosting: 'no' // Default to 'no' to exclude hosting contracts
  });
  
  // For debouncing filter changes
  const filterTimeoutRef = useRef(null);
  
  // For tracking initial render in filters effect
  const isInitialRender = useRef(true);
  
  // For tracking metrics changes for animation
  const [changedMetrics, setChangedMetrics] = useState([]);
  const prevMetricsRef = useRef({});
  
  // Internal filters state for immediate UI updates
  const [activeFilters, setActiveFilters] = useState({
    businessUnit: 'all',
    contractType: 'all',
    priority: 'all',
    hosting: 'no' // Match the default filter value
  });

  // For tracking sorting across view mode changes
  const [sortConfig, setSortConfig] = useState({
    orderBy: 'contractName',
    order: 'asc'
  });
  
  // Map column IDs between view modes to preserve sorting when switching views
  const columnMappings = {
    // Overview to Details mappings
    'contractName': 'contractName',
    'contractNumber': 'contractNumber',
    'businessUnit': null, // No equivalent in details view
    'contractType': null, // No equivalent in details view
    'mrr': null, // No equivalent in details view
    'ltv': null, // No equivalent in details view
    'priority': null, // No equivalent in details view
    'pointsBurden': null, // No equivalent in details view
    'nextInvoiceDate': null, // No equivalent in details view
    'nextTermRenewal': 'nextTermRenewal',
    
    // Details to Overview mappings
    'isInitialTerm': null, // No equivalent in overview
    'contractStart': null, // No equivalent in overview
    'currentTermStart': null, // No equivalent in overview
    'currentTermEnd': null, // No equivalent in overview
    'nextTermStart': null, // No equivalent in overview
    'nextTermEnd': null // No equivalent in overview
  };

  // Handle filter changes with debouncing
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    console.log(`Filter changed: ${name} = ${value}`);
    
    // If this is a business unit selection, log additional details
    if (name === 'businessUnit' && value && value !== 'all') {
      const selectedUnit = businessUnits.find(unit => unit.agency_id === value);
      console.log('Selected business unit details:', selectedUnit);
    }
    
    // Update UI immediately
    setActiveFilters(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear any existing timeout
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }
    
    // Set a new timeout to update actual filters after a delay
    filterTimeoutRef.current = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }, 300); // 300ms debounce
  };

  // Toggle view mode between overview and details
  const handleViewModeChange = (mode) => {
    // Save current mode for reference
    const previousMode = viewMode;
    
    // Update the view mode
    setViewMode(mode);
    
    // If sorting is active, try to preserve it across view changes
    if (sortConfig.orderBy) {
      const currentOrderBy = sortConfig.orderBy;
      
      // Find the equivalent column in the new view mode
      const mappedColumn = columnMappings[currentOrderBy];
      
      // If we're changing to a view where this column doesn't exist or has no mapping
      if (!mappedColumn) {
        // Reset table sort indicators but we'll keep the actual sort config
        // This allows us to restore the sort if the user switches back
        const tableElement = document.querySelector('.enhanced-table');
        if (tableElement) {
          // Force a brief table reload to clear visual sort indicators
          setTableLoading(true);
          setTimeout(() => {
            setTableLoading(false);
          }, 100);
        }
      }
      // The mapped column exists in the new view, sorting will be maintained by the SortablePaginatedTable
    }
  };

  // Handle sort changes from the table component
  const handleSortChange = (orderBy, order) => {
    setSortConfig({ orderBy, order });
  };

  // Add this near your other state variables
  const [isUpdatingNextInvoices, setIsUpdatingNextInvoices] = useState(false);
  
  // Add this function
  const handleUpdateNextInvoiceDates = async () => {
    if (window.confirm('This will update the Next Invoice Date for all contracts based on their upcoming invoice tasks. Continue?')) {
      try {
        setIsUpdatingNextInvoices(true);
        const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
        
        const response = await fetch(`${apiBaseUrl}/contracts/update-next-invoice-dates`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          alert(result.message);
          
          // Refresh the contracts list
          fetchContractsData(true);
        } else {
          const error = await response.json();
          alert(`Error: ${error.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error updating next invoice dates:', error);
        alert(`Failed to update next invoice dates: ${error.message}`);
      } finally {
        setIsUpdatingNextInvoices(false);
      }
    }
  };
  
  // Add this useRef to track if requests are in progress
  const isLoadingRef = useRef(false);
  
  // Create a dedicated function for fetching data that we can reuse
  const fetchContractsData = useCallback(async (isInitialLoad = false) => {
    // Prevent multiple simultaneous requests
    if (isLoadingRef.current) {
      console.log('Skipping request - another one is in progress');
      return;
    }
    
    try {
      isLoadingRef.current = true;
      
      // On initial page load, show full page loader
      // On filter change, just show table loader
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }
      setError(null);
      
      // Get current filters directly from state at the time of function call
      // This ensures we're always using the latest filter values
      const currentFilters = { ...filters };
      console.log('Current filters being used:', currentFilters);
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      
      // Build query params for filters
      const queryParams = new URLSearchParams();
      
      // Only add non-empty filter values and skip 'all' values
      if (currentFilters.businessUnit && currentFilters.businessUnit !== 'all') {
        queryParams.append('businessUnit', currentFilters.businessUnit);
      }
      
      if (currentFilters.contractType && currentFilters.contractType !== 'all') {
        queryParams.append('contractType', currentFilters.contractType);
      }
      
      if (currentFilters.priority && currentFilters.priority !== 'all') {
        queryParams.append('priority', currentFilters.priority);
      }
      
      // Always include the hosting filter, unless it's set to 'all'
      if (currentFilters.hosting !== 'all') {
        queryParams.append('hosting', currentFilters.hosting);
      }
      
      const queryString = queryParams.toString();
      
      // Fetch all data in parallel
      try {
        console.log('DEBUG START: fetchContractsData called, isInitialLoad:', isInitialLoad);
        // Get points from the admin dashboard only for unfiltered view
        let totalPointsDelivered = 0;
        const hasNoFilters = Object.keys(currentFilters).length === 0 || 
          (Object.keys(currentFilters).length === 1 && currentFilters.hosting === 'all');
        
        if (hasNoFilters) {
          console.log('DEBUG: No filters applied, getting points from admin dashboard endpoint');
          try {
            const pointsResponse = await fetch(`${apiBaseUrl}/api/tasks/points-delivered/month`);
            if (pointsResponse.ok) {
              const pointsData = await pointsResponse.json();
              totalPointsDelivered = pointsData.total || 0;
              console.log('DEBUG: Points data from admin endpoint:', totalPointsDelivered);
            } else {
              console.error('DEBUG ERROR: Failed to fetch points from admin endpoint:', pointsResponse.status);
            }
          } catch (pointsError) {
            console.error('DEBUG ERROR: Error fetching points from admin endpoint:', pointsError);
          }
        }
        
        // Always fetch metrics data (even when filters change)
        console.log(`DEBUG: Fetching metrics with filters: ${JSON.stringify(currentFilters)}`);
        const metricsUrl = `${apiBaseUrl}/api/contracts/metrics${queryString ? `?${queryString}` : ''}`;
        console.log(`DEBUG: Metrics URL: ${metricsUrl}`);
        
        const metricsResponse = await fetch(metricsUrl);
        if (!metricsResponse.ok) {
          console.error(`DEBUG ERROR: Failed to fetch metrics: ${metricsResponse.status}`);
          throw new Error(`Failed to fetch metrics: ${metricsResponse.status}`);
        }
        
        const metricsData = await metricsResponse.json();
        console.log('DEBUG: Raw metrics data received:', JSON.stringify(metricsData));
        
        // Only fetch filter options on initial load
        if (isInitialLoad) {
          // Fetch filter options
          const [businessUnitsResponse, contractTypesResponse, priorityTiersResponse] = await Promise.all([
            fetch(`${apiBaseUrl}/api/contracts/business-units`),
            fetch(`${apiBaseUrl}/api/contracts/contract-types`),
            fetch(`${apiBaseUrl}/api/contracts/priority-tiers`)
          ]);
          
          if (!businessUnitsResponse.ok) throw new Error(`Failed to fetch business units: ${businessUnitsResponse.status}`);
          if (!contractTypesResponse.ok) throw new Error(`Failed to fetch contract types: ${contractTypesResponse.status}`);
          if (!priorityTiersResponse.ok) throw new Error(`Failed to fetch priority tiers: ${priorityTiersResponse.status}`);
          
          const businessUnits = await businessUnitsResponse.json();
          const contractTypes = await contractTypesResponse.json();
          const priorityTiers = await priorityTiersResponse.json();
          
          console.log('Received business units:', businessUnits);
          console.log('Contract types:', contractTypes);
          console.log('Priority tiers:', priorityTiers);
          
          setBusinessUnits(businessUnits);
          setContractTypes(contractTypes);
          setPriorityTiers(priorityTiers);
        }
        
        // Always fetch contracts with current filters
        // Log the URL we're about to fetch
        const urlWithParams = `${apiBaseUrl}/api/contracts${queryString ? `?${queryString}` : ''}`;
        console.log(`Fetching contracts with URL: ${urlWithParams}`);
        
        const contractsResponse = await fetch(urlWithParams);
        if (!contractsResponse.ok) throw new Error(`Failed to fetch contracts: ${contractsResponse.status}`);
        
        const contractsData = await contractsResponse.json();
        console.log(`Received ${contractsData.length} contracts from API`);
        
        // DEBUG: Check if next_invoice_date is present in API response
        if (contractsData.length > 0) {
          console.log('First contract data sample:', contractsData[0]);
          console.log('next_invoice_date fields in contracts:', contractsData.map(c => ({ 
            contractName: c.contractName || c.contract_name,
            contract_id: c.contract_id || c.contractId,
            next_invoice_date: c.next_invoice_date,
            nextInvoiceDate: c.nextInvoiceDate
          })));
        }
        
        // If we have no contracts matching the filters, zero out all metrics
        // This ensures consistency between table data and metrics display
        if (contractsData.length === 0) {
          console.log('No contracts match current filters - zeroing out all metrics');
          setMetricsData({
            totalActiveContracts: 0,
            totalMRR: 0,
            averageMRR: 0,
            averageLTV: 0,
            tier1Contracts: 0,
            tier1Percentage: 0,
            totalPointsDelivered: 0
          });
        } else {
          // Contracts exist - use the metrics data from the API
          // Create a new object to ensure React detects the change
          if (hasNoFilters) {
            // Override with admin dashboard value for unfiltered view
            setMetricsData({
              ...metricsData,
              totalPointsDelivered: totalPointsDelivered
            });
            console.log(`DEBUG: Using admin dashboard value for points: ${totalPointsDelivered}`);
          } else {
            // Use the filtered value from the metrics endpoint
            setMetricsData({ ...metricsData });
            console.log(`DEBUG: Using filtered value for points: ${metricsData.totalPointsDelivered}`);
          }
        }
        
        // Fetch contract points burden data
        try {
          const pointsQueryString = queryString ? `?${queryString}` : '';
          const pointsDataResponse = await fetch(`${apiBaseUrl}/api/contracts/points-summary${pointsQueryString}`);
          if (pointsDataResponse.ok) {
            const pointsData = await pointsDataResponse.json();
            // Add points_burden data to contracts without messing with nextInvoiceDate
            const contractsWithPointsData = contractsData.map(contract => {
              const pointsInfo = pointsData.find(p => p.contract_id === contract.contractId);
              
              return {
                ...contract,
                pointsBurden: pointsInfo ? pointsInfo.points_burden : null
                // DON'T touch the nextInvoiceDate field - it's already there
              };
            });
            
            setContracts(contractsWithPointsData);
          } else {
            // If points data fetch fails, just use the contracts as they are
            setContracts(contractsData);
          }
        } catch (pointsError) {
          console.error('Error fetching points data:', pointsError);
          // Just use the contracts as they are
          setContracts(contractsData);
        }
        
      } catch (fetchError) {
        console.error('API request failed:', fetchError);
        setError(`Error loading contracts data: ${fetchError.message}`);
      }
    } catch (error) {
      console.error('Failed to fetch contracts data:', error);
      setError('Failed to load contracts data. Please try again later.');
    } finally {
      setLoading(false);
      setTableLoading(false);
      isLoadingRef.current = false;
    }
  }, [filters]); // Add filters as a dependency

  // Register the fetch function with the page visibility system with better options
  const { isVisible } = useRefreshOnFocus(fetchContractsData, [], {
    enabled: false, // Disable auto-refresh
    minTimeBetweenRefreshes: 600000 // Only refresh after 10 minutes of being away
  });
  
  // Initial data fetch
  useEffect(() => {
    fetchContractsData(true); // true indicates initial load
  }, []);
  
  // Fetch data when filters change
  useEffect(() => {
    // Skip the initial render
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    
    // Clear any existing timeout
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }
    
    // Add a timeout to prevent rapid requests on filter changes
    filterTimeoutRef.current = setTimeout(() => {
      fetchContractsData(false); // false indicates filter change, not initial load
    }, 500); // 500ms debounce
    
    // Cleanup function
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, [filters, fetchContractsData]);

  // Check if metrics changed and trigger animation
  useEffect(() => {
    console.log('DEBUG: metrics useEffect called with data:', JSON.stringify(metricsData));
    
    // Skip the initial render - but store the values
    if (isInitialRender.current) {
      console.log('DEBUG: Initial metrics render, storing values');
      prevMetricsRef.current = JSON.parse(JSON.stringify(metricsData));
      isInitialRender.current = false;
      return;
    }
    
    // Debug logs to track points changes
    console.log('DEBUG: Points comparison:',
      'Previous:', prevMetricsRef.current.totalPointsDelivered,
      'Current:', metricsData.totalPointsDelivered);
    
    // Compare with previous metrics and track changes
    const changed = [];
    if (prevMetricsRef.current.totalActiveContracts !== metricsData.totalActiveContracts) {
      changed.push('totalActiveContracts');
    }
    if (prevMetricsRef.current.totalMRR !== metricsData.totalMRR) {
      changed.push('totalMRR');
    }
    if (prevMetricsRef.current.averageMRR !== metricsData.averageMRR) {
      changed.push('averageMRR');
    }
    if (prevMetricsRef.current.averageLTV !== metricsData.averageLTV) {
      changed.push('averageLTV');
    }
    if (prevMetricsRef.current.tier1Percentage !== metricsData.tier1Percentage) {
      changed.push('tier1Percentage');
    }
    if (prevMetricsRef.current.tier1Contracts !== metricsData.tier1Contracts) {
      changed.push('tier1Contracts');
    }
    
    // Explicitly check if points value changed
    const pointsChanged = 
      prevMetricsRef.current.totalPointsDelivered !== metricsData.totalPointsDelivered;
    
    if (pointsChanged) {
      console.log(`DEBUG: Points value changed: ${prevMetricsRef.current.totalPointsDelivered} ? ${metricsData.totalPointsDelivered}`);
      changed.push('totalPointsDelivered');
    }
    
    // Store current metrics as previous for next comparison
    // Use deep copy to avoid reference issues
    prevMetricsRef.current = JSON.parse(JSON.stringify(metricsData));
    
    // Set changed metrics to trigger animation
    if (changed.length > 0) {
      console.log('DEBUG: Animated metrics:', changed);
      setChangedMetrics(changed);
      
      // Clear changed metrics after animation duration
      const timer = setTimeout(() => {
        setChangedMetrics([]);
      }, 1500); // Animation duration + small buffer
      return () => clearTimeout(timer);
    }
  }, [metricsData]);

  return (
    <MainLayout title="Contracts">
      {loading ? (
        <div className="loading-container">
          <CircularProgress />
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <p className="error-details">
            Check the console for more details or contact your administrator.
          </p>
        </div>
      ) : (
        <>
          {showAddContract ? (
            <div className="scrollable-content">
              <Suspense fallback={<div className="loading-container"><CircularProgress /></div>}>
                <FormContainer onCancel={() => setShowAddContract(false)} />
              </Suspense>
            </div>
          ) : (
            <div className="scrollable-content">
              <div className="stats-grid six-cards">
                {/* Total Active Contracts */}
                <div className={`stat-card ${changedMetrics.includes('totalActiveContracts') ? 'changed' : ''}`}>
                  <div className="stat-label">Total Active Contracts</div>
                  <div className="stat-value">{metricsData.totalActiveContracts}</div>
                </div>
                
                {/* Total MRR */}
                <div className={`stat-card ${changedMetrics.includes('totalMRR') ? 'changed' : ''}`}>
                  <div className="stat-label">Total MRR</div>
                  <div className="stat-value">{formatCurrency(metricsData.totalMRR)}</div>
                </div>
                
                {/* Average MRR */}
                <div className={`stat-card ${changedMetrics.includes('averageMRR') ? 'changed' : ''}`}>
                  <div className="stat-label">Average MRR</div>
                  <div className="stat-value">{formatCurrency(metricsData.averageMRR)}</div>
                </div>
                
                {/* Average LTV */}
                <div className={`stat-card ${changedMetrics.includes('averageLTV') ? 'changed' : ''}`}>
                  <div className="stat-label">Average LTV</div>
                  <div className="stat-value">{formatCurrency(metricsData.averageLTV)}</div>
                </div>
                
                {/* % Tier 1 */}
                <div className={`stat-card ${changedMetrics.includes('tier1Percentage') ? 'changed' : ''}`}>
                  <div className="stat-label">% Tier 1</div>
                  <div className="stat-value">{Math.round(metricsData.tier1Percentage)}%</div>
                </div>
                
                {/* Total Points Delivered This Month */}
                <div className={`stat-card ${changedMetrics.includes('totalPointsDelivered') ? 'changed' : ''}`}>
                  <div className="stat-label">Total Points Delivered This Month</div>
                  <div className="stat-value">{metricsData.totalPointsDelivered || 0}</div>
                </div>
              </div>
              
              {/* Filters and View Toggle in one row */}
              <div className="filters-row">
                {/* Toggle on the left */}
                <div className="view-toggle-container">
                  <div className="toggle-label">View Mode</div>
                  <div className="view-toggle">
                    <button 
                      className={`view-toggle-button ${viewMode === 'overview' ? 'active' : ''}`}
                      onClick={() => handleViewModeChange('overview')}
                    >
                      Overview
                    </button>
                    <button 
                      className={`view-toggle-button ${viewMode === 'details' ? 'active' : ''}`}
                      onClick={() => handleViewModeChange('details')}
                    >
                      Details
                    </button>
                  </div>
                </div>

                {/* Filters on the right */}
                <div className="filters-container">
                  <Grid container spacing={2}>
                    {/* Business Unit Filter */}
                    <Grid item xs={12} md={3}>
                      <CustomSelect
                        label="Business Units"
                        name="businessUnit"
                        value={activeFilters.businessUnit}
                        onChange={handleFilterChange}
                        options={[
                          { value: 'all', label: 'All Business Units' },
                          ...businessUnits.map(unit => ({
                            value: unit.agency_id,
                            label: unit.name
                          }))
                        ]}
                      />
                    </Grid>
                    
                    {/* Contract Types Filter */}
                    <Grid item xs={12} md={3}>
                      <CustomSelect
                        label="Contract Types"
                        name="contractType"
                        value={activeFilters.contractType}
                        onChange={handleFilterChange}
                        options={[
                          { value: 'all', label: 'All Contract Types' },
                          ...contractTypes.map(type => ({
                            value: type.value,
                            label: type.label.charAt(0).toUpperCase() + type.label.slice(1)
                          }))
                        ]}
                      />
                    </Grid>
                    
                    {/* Priority Filter */}
                    <Grid item xs={12} md={3}>
                      <CustomSelect
                        label="Priority"
                        name="priority"
                        value={activeFilters.priority}
                        onChange={handleFilterChange}
                        options={[
                          { value: 'all', label: 'All Priorities' },
                          ...priorityTiers.map(tier => ({
                            value: tier.value,
                            label: tier.label
                          }))
                        ]}
                      />
                    </Grid>
                    
                    {/* Hosting Filter */}
                    <Grid item xs={12} md={3}>
                      <CustomSelect
                        label="Hosting"
                        name="hosting"
                        value={activeFilters.hosting}
                        onChange={handleFilterChange}
                        options={[
                          { value: 'all', label: 'All Contracts' },
                          { value: 'yes', label: 'Hosting Only' },
                          { value: 'no', label: 'Non-Hosting Only' }
                        ]}
                      />
                    </Grid>
                  </Grid>
                </div>
              </div>
              
              {/* Contracts Table - Replace with SortablePaginatedTable */}
              <div className={`contracts-table-container ${viewMode}-mode`}>
                {tableLoading && (
                  <Box sx={{ width: '100%', position: 'relative', mb: 1 }}>
                    <Fade in={tableLoading} style={{ transitionDelay: tableLoading ? '300ms' : '0ms' }}>
                      <LinearProgress color="secondary" />
                    </Fade>
                  </Box>
                )}
                
                <SortablePaginatedTable
                  data={contracts}
                  columns={viewMode === 'overview' ? overviewColumns : detailsColumns}
                  rowsPerPage={10}
                  emptyMessage={tableLoading ? 'Loading contracts...' : 'No contracts found with the selected filters.'}
                  tableContainerProps={{ className: "enhanced-table" }}
                  tableProps={{ stickyHeader: true }}
                  onSortChange={handleSortChange}
                  initialOrderBy={sortConfig.orderBy}
                  initialOrder={sortConfig.order}
                  isContractDetailTable={false}
                  stickyColumn="contractName"
                />
              </div>
              
              {/* Add Contract Button - Moved to bottom right */}
              <div className="contracts-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <Button
                  variant="contained"
                  color="primary" 
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddContract(true)}
                  className="add-contract-button"
                >
                  Add Contract
                </Button>
              </div>

              {/* Add this button in your UI near other action buttons or filters */}
              {/* For example, add this in the Contracts list view header or in the filters area */}
              <Box sx={{ ml: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleUpdateNextInvoiceDates}
                  disabled={isUpdatingNextInvoices}
                  size="small"
                  startIcon={isUpdatingNextInvoices ? <CircularProgress size={20} /> : null}
                >
                  {isUpdatingNextInvoices ? 'Updating...' : 'Update Next Invoice Dates'}
                </Button>
              </Box>
            </div>
          )}
        </>
      )}
    </MainLayout>
  );
}; 